import React, { useEffect, useMemo, useState } from 'react'
import { Button, Card, Form, Modal } from 'react-bootstrap'
import { FaCaretDown, FaCaretUp } from 'react-icons/fa'

import AdminPicker from '../../../components/AdminPicker/AdminPicker'
import { useAuth, useProtectedContent, useProtectedPage } from '../../../context/AuthContext'
import callAPI from '../../../helpers/callAPI'
import Advertiser from '../../../templates/Advertiser/Advertiser'
import Dollars from '../../../templates/Dollars/Dollars'
import ReportTable from '../../../templates/ReportTable/ReportTable'
import Loading from '../../../templates/UI/Loading/Loading'

const DEFAULT_SEGMENTS = ['Growth', 'Retain', 'Risk']
const NULL_SEGMENT_VALUE = '__NULL__'
const ALL_SEGMENT_VALUE = 'ALL'

const isNullSegment = (segment) => {
    return segment === null || segment === undefined || segment === '' || String(segment).toUpperCase() === 'NULL'
}

const formatSegment = (segment) => {
    if (segment === null || segment === undefined || segment === '') {
        return 'NULL'
    }

    return segment
}

const toNumber = (value) => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0
    }

    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
}

const getEarningsChangePercent = (currentEarnings, previousEarnings) => {
    const current = toNumber(currentEarnings)
    const previous = toNumber(previousEarnings)

    if (previous === 0) {
        return null
    }

    return ((current - previous) / Math.abs(previous)) * 100
}

const formatPercentChange = (value) => {
    const numericValue = Math.abs(toNumber(value))

    if (Number.isInteger(numericValue)) {
        return `${numericValue}%`
    }

    return `${numericValue.toFixed(2)}%`
}

const getAdvertiserManagerName = (row = {}) => row.manager_name || row.ad_manager_name || row.mer_mgr || row.am_name || ''

const AdvertiserSegmentOverrideModal = ({ isOpen, setIsOpen, advertiserId, onSaved, showSystemMessage }) => {
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [details, setDetails] = useState(null)
    const [segment, setSegment] = useState('')
    const [segmentOverride, setSegmentOverride] = useState('N')

    useEffect(() => {
        if (!isOpen || !advertiserId) {
            return
        }

        const loadAdvertiserDetails = async () => {
            setLoading(true)

            const result = await callAPI(`advertiser/${advertiserId}/details`, 'GET')

            if (!result.success) {
                showSystemMessage('error', result.errors.message || 'Unable to load advertiser settings.')
                setLoading(false)
                return
            }

            setDetails(result)
            setSegment(result.general_info.segment ?? '')
            setSegmentOverride(result.general_info.segment_override ?? 'N')
            setLoading(false)
        }

        loadAdvertiserDetails()
    }, [isOpen, advertiserId, showSystemMessage])

    const handleClose = () => {
        if (saving) {
            return
        }

        setIsOpen(false)
        setDetails(null)
    }

    const handleSave = async () => {
        if (!details || !details.general_info) {
            return
        }

        setSaving(true)

        const putBody = {
            name: details.general_info.name,
            status: details.general_info.status,
            ad_manager: details.general_info.merchant_mgr_id,
            bizdev_manager: details.general_info.bizdev_mgr_id,
            network_split_affsub: details.general_info.network_split_affsub,
            multiple_postback: details.general_info.supports_multiple_postback,
            segment,
            segment_override: segmentOverride,
            platform_id: details.general_info.merchant_platform_id,
            aliases: details.aliases || [],
            contract_url: details.general_info.contract_url || '',
            agreement_sign_date: details.general_info.agreement_sign_date || '',
        }

        const saveResult = await callAPI(`advertiser/${advertiserId}/generalInfo`, 'PUT', putBody)

        setSaving(false)

        if (!saveResult.success) {
            showSystemMessage('error', saveResult.errors.message || 'Unable to save segment override.')
            return
        }

        showSystemMessage('success', `Advertiser ${advertiserId} segment override updated.`)
        setIsOpen(false)
        setDetails(null)
        onSaved()
    }

    return (
        <Modal show={isOpen} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Update Segment Override</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <Loading />
                ) : (
                    <>
                        <div className="mb-2 text-small text-muted">Advertiser ID: {advertiserId}</div>
                        <Form.Group>
                            <Form.Label>Segment</Form.Label>
                            <Form.Control size="sm" as="select" value={segment} onChange={(e) => setSegment(e.target.value)}>
                                <option value="">-</option>
                                {DEFAULT_SEGMENTS.map((item) => {
                                    return (
                                        <option key={item} value={item}>
                                            {item}
                                        </option>
                                    )
                                })}
                            </Form.Control>
                        </Form.Group>
                        <Form.Group className="mb-0">
                            <Form.Label>Segment Mode</Form.Label>
                            <Form.Control size="sm" as="select" value={segmentOverride} onChange={(e) => setSegmentOverride(e.target.value)}>
                                <option value="Y">Mode: Override</option>
                                <option value="N">Mode: Dynamic</option>
                            </Form.Control>
                        </Form.Group>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={saving}>
                    Cancel
                </Button>
                <Button variant="warning" onClick={handleSave} disabled={loading || saving}>
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

const AdvertiserSegmentReport = ({ showSystemMessage }) => {
    document.title = 'Advertiser Segment Report'

    const auth = useAuth()
    useProtectedPage(auth.permissions, 'advertisers_list')
    const canEditSegment = useProtectedContent(auth.permissions, 'advertiser_segment_edit')

    const [mode, setMode] = useState('segment')
    const [segment, setSegment] = useState(DEFAULT_SEGMENTS[0])
    const [adminId, setAdminId] = useState(0)

    const [report, setReport] = useState([])
    const [segments, setSegments] = useState(DEFAULT_SEGMENTS)
    const [dataLoaded, setDataLoaded] = useState(false)

    const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false)
    const [workingAdvertiserId, setWorkingAdvertiserId] = useState(null)

    const fetchReport = async () => {
        setDataLoaded(false)

        let url = `advertisers/segmentReport?mode=${mode}`

        if (mode === 'segment') {
            url += `&segment=${encodeURIComponent(segment)}`
        }

        if (mode === 'manager' && adminId > 0) {
            url += `&adminId=${adminId}`
            url += '&assignedOnly=Y'
        }

        const result = await callAPI(url, 'GET')

        if (!result.success) {
            showSystemMessage('error', result.errors.message || 'Unable to load advertiser segment report.')
            setReport([])
            setDataLoaded(true)
            return
        }

        const rows = Array.isArray(result.report)
            ? result.report
            : Array.isArray(result.advertisers)
                ? result.advertisers
                : Array.isArray(result.rows)
                    ? result.rows
                    : []

        const filteredRows = mode === 'segment' && segment === ALL_SEGMENT_VALUE
            ? rows.filter((row) => !isNullSegment(row.segment))
            : rows

        const segmentList = Array.isArray(result.segments) && result.segments.length > 0 ? result.segments : DEFAULT_SEGMENTS

        setSegments(segmentList)
        setReport(filteredRows)
        setDataLoaded(true)
    }

    useEffect(() => {
        if (mode === 'manager' && Number(adminId) <= 0) {
            setReport([])
            setDataLoaded(true)
            return
        }

        fetchReport()
    }, [mode, segment, adminId])

    const rowsWithComputedChange = useMemo(() => {
        return report.map((row) => {
            const current30D = toNumber(row.network_earnings_30d)
            const previous30D = toNumber(row.prev_network_earnings_30d)
            const changePercent = getEarningsChangePercent(current30D, previous30D)

            const currMonth = toNumber(row.network_earnings_curr_month)
            const prevMonth = toNumber(row.network_earnings_prev_month)
            const monthChangePercent = getEarningsChangePercent(currMonth, prevMonth)

            return {
                ...row,
                manager_name: getAdvertiserManagerName(row),
                network_earnings_30d: current30D,
                prev_network_earnings_30d: previous30D,
                earnings_change_percent: changePercent,
                network_earnings_curr_month: currMonth,
                network_earnings_prev_month: prevMonth,
                month_earnings_change_percent: monthChangePercent,
            }
        })
    }, [report])

    const columns = useMemo(() => {
        return [
            {
                Header: 'Advertiser ID',
                accessor: 'merchant_id',
                Cell: ({ cell: { row } }) => {
                    return canEditSegment ? (
                        <Button
                            variant="link"
                            className="p-0"
                            onClick={() => {
                                setWorkingAdvertiserId(row.original.merchant_id)
                                setIsOverrideModalOpen(true)
                            }}
                        >
                            {row.original.merchant_id}
                        </Button>
                    ) : (
                        <>{row.original.merchant_id}</>
                    )
                },
            },
            {
                Header: 'Name',
                accessor: 'name',
                Cell: ({ cell: { row } }) => {
                    return <Advertiser id={row.original.merchant_id} name={row.original.name} status={row.original.status} />
                },
            },
            {
                Header: 'Manager',
                accessor: 'manager_name',
                Cell: ({ cell: { value } }) => {
                    return <>{value || 'N/A'}</>
                },
            },
            {
                Header: 'Segment',
                accessor: 'segment',
                Cell: ({ cell: { value } }) => {
                    return <>{formatSegment(value)}</>
                },
            },
            {
                Header: 'Segment Override',
                accessor: 'segment_override',
                Cell: ({ cell: { value } }) => {
                    if (value === 'Y') {
                        return <>Y (Override)</>
                    }

                    if (value === 'N') {
                        return <>N (Dynamic)</>
                    }

                    return <>{value || 'N/A'}</>
                },
            },
            {
                Header: () => <div className="text-right">30D Network Earnings</div>,
                accessor: 'network_earnings_30d',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: () => <div className="text-right">Prev30D Network Earnings</div>,
                accessor: 'prev_network_earnings_30d',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: () => <div className="text-right">Network Earnings Change</div>,
                accessor: 'earnings_change_percent',
                className: 'text-right',
                Cell: ({ cell: { value, row } }) => {
                    if (value === null || value === undefined) {
                        return <>$0 (0%)</>
                    }

                    const dollarChange = toNumber(row.original.network_earnings_30d) - toNumber(row.original.prev_network_earnings_30d)
                    const isPositive = dollarChange >= 0
                    const colorClass = isPositive ? 'text-success' : 'text-danger'

                    return (
                        <span className={colorClass}>
                            {isPositive ? <FaCaretUp /> : <FaCaretDown />}{' '}
                            <Dollars value={Math.abs(dollarChange)} /> ({formatPercentChange(value)})
                        </span>
                    )
                },
            },
            {
                Header: () => <div className="text-right">Curr Month Earnings</div>,
                accessor: 'network_earnings_curr_month',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: () => <div className="text-right">Prev Month Earnings</div>,
                accessor: 'network_earnings_prev_month',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: () => <div className="text-right">Month Earnings Change</div>,
                accessor: 'month_earnings_change_percent',
                className: 'text-right',
                Cell: ({ cell: { value, row } }) => {
                    if (value === null || value === undefined) {
                        return <>$0 (0%)</>
                    }

                    const dollarChange = toNumber(row.original.network_earnings_curr_month) - toNumber(row.original.network_earnings_prev_month)
                    const isPositive = dollarChange >= 0
                    const colorClass = isPositive ? 'text-success' : 'text-danger'

                    return (
                        <span className={colorClass}>
                            {isPositive ? <FaCaretUp /> : <FaCaretDown />}{' '}
                            <Dollars value={Math.abs(dollarChange)} /> ({formatPercentChange(value)})
                        </span>
                    )
                },
            },
        ]
    }, [canEditSegment])

    const noDataMessage = mode === 'manager' && Number(adminId) <= 0
        ? 'Choose an Ad Manager to load advertisers with assigned segments.'
        : 'No advertisers found for the current filters.'

    return (
        <>
            <AdvertiserSegmentOverrideModal
                isOpen={isOverrideModalOpen}
                setIsOpen={setIsOverrideModalOpen}
                advertiserId={workingAdvertiserId}
                onSaved={fetchReport}
                showSystemMessage={showSystemMessage}
            />

            <div className="page-header d-flex align-items-center">
                <h1 className="mb-h1">Advertiser Segment Report</h1>
            </div>

            <Card>
                <Card.Header>
                    <div className="d-flex flex-wrap align-items-end">
                        <div className="mr-3">
                            <div className="text-small mb-1 font-weight-bold">Mode</div>
                            <Form.Control size="sm" as="select" value={mode} onChange={(e) => setMode(e.target.value)}>
                                <option value="segment">By Segment</option>
                                <option value="manager">By Ad Manager</option>
                            </Form.Control>
                        </div>

                        {mode === 'segment' && (
                            <div className="mr-3">
                                <div className="text-small mb-1 font-weight-bold">Segment</div>
                                <Form.Control size="sm" as="select" value={segment} onChange={(e) => setSegment(e.target.value)}>
                                    <option value={ALL_SEGMENT_VALUE}>ALL</option>
                                    {segments.map((item) => {
                                        return (
                                            <option key={item} value={item}>
                                                {item}
                                            </option>
                                        )
                                    })}
                                    <option value={NULL_SEGMENT_VALUE}>Not Assigned</option>
                                </Form.Control>
                            </div>
                        )}

                        {mode === 'manager' && (
                            <div className="mr-3">
                                <div className="text-small mb-1 font-weight-bold">Ad Manager</div>
                                <AdminPicker
                                    adminId={adminId}
                                    setAdminId={setAdminId}
                                    role={['MERMGR', 'ADOPS_MANAGER', 'ADOPS_TEAMLEAD', 'BIZDEV', 'BIZDEV_DIRECTOR', 'ADVERTISER_DIRECTOR']}
                                    placeholder="Select manager"
                                    required
                                />
                            </div>
                        )}
                    </div>
                </Card.Header>

                <Card.Body className="p-0">
                    {dataLoaded ? (
                        rowsWithComputedChange.length === 0 ? (
                            <div className="p-3">{noDataMessage}</div>
                        ) : (
                            <ReportTable
                                data={rowsWithComputedChange}
                                columns={columns}
                                getRowProps={(row) => {
                                    const classes = row.original.earnings_change_percent !== null && row.original.earnings_change_percent <= -10 ? 'bgDanger25' : ''

                                    return {
                                        className: classes,
                                    }
                                }}
                            />
                        )
                    ) : (
                        <Loading />
                    )}
                </Card.Body>
            </Card>
        </>
    )
}

export default AdvertiserSegmentReport
